import React, { useState, useEffect, useCallback } from 'react';
import Pagination from './Pagination';
import './AccountActivities.css';

const API_BASE = '/api';

function AccountActivities() {
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('tumu'); // tumu | deposit | withdraw | betplay | betwin
  const [users, setUsers] = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Filters
  const _now = new Date();
  const _y = _now.getFullYear(), _m = String(_now.getMonth() + 1).padStart(2, '0'), _d = String(_now.getDate()).padStart(2, '0');
  const [filterStartDate, setFilterStartDate] = useState(`${_y}-${_m}-01`);
  const [filterEndDate, setFilterEndDate] = useState(`${_y}-${_m}-${_d}`);
  const [filterSearch, setFilterSearch] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);

  const token = localStorage.getItem('token');

  const formatMoney = (val) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '0,00';
    return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Fetch user list
  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/account-activities/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setUsers(data.users || []);
        setDisplayNameMap(data.displayNameMap || {});
        setCurrentUser(data.currentUser);
        setIsLoggedIn(data.isLoggedIn);
        // selectedUsers stays empty = Tümü (all users)
      }
    } catch (err) { console.error('Users fetch error:', err); }
  }, [token]);

  // Fetch activities from DB
  const fetchActivities = useCallback(async (page = 1, useFilters = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 50);
      if (selectedUsers.length > 0) params.set('nesineUser', selectedUsers.join(','));

      // Tab-based activity type filter
      if (tab === 'deposit') params.set('activityType', '5');
      else if (tab === 'withdraw') params.set('activityType', '9');
      else if (tab === 'betplay' || tab === 'betwin') params.set('activityType', '1');

      if (useFilters || filtersApplied) {
        if (filterStartDate) params.set('startDate', filterStartDate);
        if (filterEndDate) params.set('endDate', filterEndDate);
        if (filterSearch.trim()) params.set('search', filterSearch.trim());
      }

      const res = await fetch(`${API_BASE}/account-activities/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        let filtered = data.data;
        // For betplay/betwin, further filter by amount sign on client side
        if (tab === 'betplay') {
          filtered = filtered.filter(a => {
            const amt = parseTL(a.amount);
            return amt < 0;
          });
        } else if (tab === 'betwin') {
          filtered = filtered.filter(a => {
            const amt = parseTL(a.amount);
            return amt > 0;
          });
        }
        setActivities(filtered);
        setPagination(data.pagination);
      }
    } catch (err) { console.error('Activities fetch error:', err); }
    setLoading(false);
  }, [token, tab, selectedUsers, filtersApplied, filterStartDate, filterEndDate, filterSearch]); // eslint-disable-line

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedUsers.length > 0) params.set('nesineUser', selectedUsers.join(','));
      const res = await fetch(`${API_BASE}/account-activities/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) { console.error('Stats fetch error:', err); }
  }, [token, selectedUsers]);

  // Sync from Nesine API
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API_BASE}/account-activities/sync?all=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.multi && Array.isArray(data.results)) {
        // Çoklu hesap sonucu
        const summary = data.results.map(r => {
          if (!r.success) return `${displayNameMap[r.nesineUser] || r.nesineUser || '-'}: HATA (${r.error})`;
          const parts = [`${displayNameMap[r.nesineUser] || r.nesineUser}: ${r.totalNew} yeni kayıt`];
          parts.push(`${r.pagesFetched} sayfa`);
          parts.push(`DB: ${r.totalInDB}`);
          if (r.rateLimitHit) parts.push('⚠️ Rate limit');
          return parts.join(', ');
        }).join(' | ');
        setSyncResult(summary);
        fetchActivities(1, filtersApplied);
        fetchStats();
      } else if (data.success) {
        // Tek hesap sonucu (eski davranış)
        const parts = [`${data.totalNew} yeni kayıt`];
        parts.push(`${data.pagesFetched} sayfa çekildi`);
        parts.push(`DB'de toplam ${data.totalInDB} kayıt`);
        if (data.rateLimitHit) parts.push('⚠️ Rate limit');
        setSyncResult(parts.join(' · '));
        fetchActivities(1, filtersApplied);
        fetchStats();
      } else {
        setSyncResult(data.error || 'Senkronizasyon hatası');
      }
    } catch (err) {
      setSyncResult('Senkronizasyon hatası: ' + err.message);
    }
    setSyncing(false);
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    fetchActivities(1, filtersApplied);
    fetchStats();
  }, [tab, selectedUsers]); // eslint-disable-line

  const applyFilters = () => {
    setFiltersApplied(true);
    fetchActivities(1, true);
  };

  const clearFilters = () => {
    const now = new Date();
    const yy = now.getFullYear(), mm = String(now.getMonth() + 1).padStart(2, '0'), dd = String(now.getDate()).padStart(2, '0');
    setFilterStartDate(`${yy}-${mm}-01`);
    setFilterEndDate(`${yy}-${mm}-${dd}`);
    setFilterSearch('');
    setFiltersApplied(false);
    fetchActivities(1, false);
  };

  const handlePageChange = (page) => {
    fetchActivities(page, filtersApplied);
  };

  const parseTL = (s) => {
    if (!s) return 0;
    const cleaned = s.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const descBadge = (desc, rt, amount) => {
    const amt = parseTL(amount);
    if (rt === 5) return <span className="aa-desc-badge aa-desc-deposit">{desc}</span>;
    if (rt === 9) return <span className="aa-desc-badge aa-desc-withdraw">{desc}</span>;
    if (rt === 1 && amt < 0) return <span className="aa-desc-badge aa-desc-betplay">{desc}</span>;
    if (rt === 1 && amt >= 0) return <span className="aa-desc-badge aa-desc-betwin">{desc}</span>;
    return <span className="aa-desc-badge aa-desc-other">{desc}</span>;
  };

  return (
    <div className="account-activities">
      {/* Header */}
      <div className="aa-header">
        <div className="aa-header-left">
          <h2>💰 Hesap Hareketleri</h2>
        </div>
        <div className="aa-header-right">
          <button className="aa-sync-btn" onClick={handleSync} disabled={syncing}>
            {syncing ? '⏳ Senkronize ediliyor...' : '🔄 Senkronize Et'}
          </button>
        </div>
      </div>

      {/* User Toggle Buttons */}
      {users.length > 0 && (
        <div className="ca-user-btns">
          <button className={`ca-user-btn ${selectedUsers.length === 0 ? 'active' : ''}`} onClick={() => setSelectedUsers([])}>
            Tümü
          </button>
          {users.map(u => (
            <button key={u} className={`ca-user-btn ${selectedUsers.includes(u) ? 'active' : ''}`} onClick={() => {
              setSelectedUsers(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);
            }}>
              {displayNameMap[u] || u}{u === currentUser ? ' ●' : ''}
            </button>
          ))}
        </div>
      )}

      {syncResult && <div className="aa-sync-result">{syncResult}</div>}

      {/* Stats */}
      {stats && (
        <div className="aa-stats">
          <div className="aa-stat-card aa-stat-deposit">
            <div className="aa-stat-label">Para Yatırma</div>
            <div className="aa-stat-value">{formatMoney(stats.totalDeposit)} TL</div>
            <div className="aa-stat-sub">{stats.depositCount} işlem</div>
          </div>
          <div className="aa-stat-card aa-stat-withdraw">
            <div className="aa-stat-label">Para Çekme</div>
            <div className="aa-stat-value">{formatMoney(stats.totalWithdraw)} TL</div>
            <div className="aa-stat-sub">{stats.withdrawCount} işlem</div>
          </div>
          <div className="aa-stat-card aa-stat-betplay">
            <div className="aa-stat-label">Kupon Oynama</div>
            <div className="aa-stat-value">{formatMoney(stats.totalBetPlay)} TL</div>
            <div className="aa-stat-sub">{stats.betPlayCount} işlem</div>
          </div>
          <div className="aa-stat-card aa-stat-betwin">
            <div className="aa-stat-label">Kupon Kazanç</div>
            <div className="aa-stat-value">{formatMoney(stats.totalBetWin)} TL</div>
            <div className="aa-stat-sub">{stats.betWinCount} işlem</div>
          </div>
          <div className={`aa-stat-card ${parseFloat(stats.netBalance) >= 0 ? 'aa-stat-net-profit' : 'aa-stat-net-loss'}`}>
            <div className="aa-stat-label">Net Kazanç</div>
            <div className="aa-stat-value">{formatMoney(stats.netBalance)} TL</div>
            <div className="aa-stat-sub">{stats.totalCount} toplam işlem</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="aa-tabs">
        <button className={`aa-tab ${tab === 'tumu' ? 'aa-tab-active' : ''}`} onClick={() => setTab('tumu')}>
          📋 Tümü
        </button>
        <button className={`aa-tab ${tab === 'deposit' ? 'aa-tab-active' : ''}`} onClick={() => setTab('deposit')}>
          💵 Para Yatırma
        </button>
        <button className={`aa-tab ${tab === 'withdraw' ? 'aa-tab-active' : ''}`} onClick={() => setTab('withdraw')}>
          💸 Para Çekme
        </button>
        <button className={`aa-tab ${tab === 'betplay' ? 'aa-tab-active' : ''}`} onClick={() => setTab('betplay')}>
          🎰 Kupon Oynama
        </button>
        <button className={`aa-tab ${tab === 'betwin' ? 'aa-tab-active' : ''}`} onClick={() => setTab('betwin')}>
          🏆 Kupon Kazanç
        </button>
      </div>

      {/* Filters */}
      <div className="aa-filters">
        <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="aa-filter-input" />
        <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="aa-filter-input" />
        <input type="text" placeholder="Açıklama ara..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} className="aa-filter-input" />
        <button className="aa-filter-btn" onClick={applyFilters}>🔍 Filtrele</button>
        {filtersApplied && <button className="aa-filter-clear" onClick={clearFilters}>✕ Temizle</button>}
      </div>

      {/* Top Pagination */}
      {pagination.totalPages > 1 && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          onPageChange={handlePageChange}
        />
      )}

      {/* Table */}
      <div className="aa-table-container">
        {loading ? (
          <div className="aa-loading">Yükleniyor...</div>
        ) : (
          <table className="aa-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Hesap</th>
                <th>Açıklama</th>
                <th>Kupon No</th>
                <th>Tutar</th>
                <th>Bakiye</th>
              </tr>
            </thead>
            <tbody>
              {activities.length === 0 ? (
                <tr><td colSpan="6" className="aa-empty">
                  Hesap hareketi bulunamadı — Senkronize Et butonunu kullanın
                </td></tr>
              ) : (
                activities.map((a, i) => {
                  const amt = parseTL(a.amount);
                  return (
                    <tr key={a._id || i}>
                      <td className="aa-td-date">{a.tt}</td>
                      <td className="aa-td-account">{displayNameMap[a.nesineUser] || a.nesineUser || '—'}</td>
                      <td className="aa-td-desc">{descBadge(a.description, a.rt, a.amount)}</td>
                      <td className="aa-td-cid">{a.cid || '-'}</td>
                      <td className={`aa-td-amount ${amt >= 0 ? 'aa-amount-positive' : 'aa-amount-negative'}`}>
                        {a.amount}
                      </td>
                      <td className="aa-td-balance">{a.balance}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Bottom Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ marginTop: 16 }}>
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}

export default AccountActivities;
